import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import {
  stakeholder as stakeholderSchema,
  stakeholderDraft as stakeholderDraftSchema,
} from '../lib/schema.js';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { selectEntities } from './entities.js';

// API Actions

const RETRIEVE_STAKEHOLDERS_REQUEST =
  'changesync/stakeholders/RETRIEVE_STAKEHOLDERS_REQUEST';
const RETRIEVE_STAKEHOLDERS_SUCCESS =
  'changesync/stakeholders/RETRIEVE_STAKEHOLDERS_SUCCESS';
const RETRIEVE_STAKEHOLDERS_FAILURE =
  'changesync/stakeholders/RETRIEVE_STAKEHOLDERS_FAILURE';
const DELETE_STAKEHOLDER_REQUEST =
  'changesync/stakeholders/DELETE_STAKEHOLDER_REQUEST';
const DELETE_STAKEHOLDER_SUCCESS =
  'changesync/stakeholders/DELETE_STAKEHOLDER_SUCCESS';
const DELETE_STAKEHOLDER_FAILURE =
  'changesync/stakeholders/DELETE_STAKEHOLDER_FAILURE';
const DOWNLOAD_REPORT_REQUEST =
  'changesync/stakeholders/DOWNLOAD_REPORT_REQUEST';
const DOWNLOAD_REPORT_SUCCESS =
  'changesync/stakeholders/DOWNLOAD_REPORT_SUCCESS';
const DOWNLOAD_REPORT_FAILURE =
  'changesync/stakeholders/DOWNLOAD_REPORT_FAILURE';
const UPDATE_STAKEHOLDER_POSITION_REQUEST =
  'changesync/stakeholders/UPDATE_STAKEHOLDER_POSITION_REQUEST';
const UPDATE_STAKEHOLDER_POSITION_SUCCESS =
  'changesync/stakeholders/UPDATE_STAKEHOLDER_POSITION_SUCCESS';
const UPDATE_STAKEHOLDER_POSITION_FAILURE =
  'changesync/stakeholders/UPDATE_STAKEHOLDER_POSITION_FAILURE';

const CREATE_UPDATE_STAKEHOLDER_DRAFT_REQUEST =
  'changesync/stakeholders/CREATE_UPDATE_STAKEHOLDER_DRAFT_REQUEST';
const CREATE_UPDATE_STAKEHOLDER_DRAFT_SUCCESS =
  'changesync/stakeholders/CREATE_UPDATE_STAKEHOLDER_DRAFT_SUCCESS';
const CREATE_UPDATE_STAKEHOLDER_DRAFT_FAILURE =
  'changesync/stakeholders/CREATE_UPDATE_STAKEHOLDER_DRAFT_FAILURE';

const FINISH_UPDATE_STAKEHOLDER_DRAFT_REQUEST =
  'changesync/stakeholders/FINISH_UPDATE_STAKEHOLDER_DRAFT_REQUEST';
const FINISH_UPDATE_STAKEHOLDER_DRAFT_SUCCESS =
  'changesync/stakeholders/FINISH_UPDATE_STAKEHOLDER_DRAFT_SUCCESS';
const FINISH_UPDATE_STAKEHOLDER_DRAFT_FAILURE =
  'changesync/stakeholders/FINISH_UPDATE_STAKEHOLDER_DRAFT_FAILURE';
const RESET_DRAFT_ID = 'changesync/stakeholders/RESET_DRAFT_ID';

// Search API Actions
const SEARCH_STAKEHOLDERS_REQUEST =
  'changesync/stakeholders/SEARCH_STAKEHOLDERS_REQUEST';
const SEARCH_STAKEHOLDERS_SUCCESS =
  'changesync/stakeholders/SEARCH_STAKEHOLDERS_SUCCESS';
const SEARCH_STAKEHOLDERS_FAILURE =
  'changesync/stakeholders/SEARCH_STAKEHOLDERS_FAILURE';

// Search Actions
const UPDATE_SEARCH_QUERY = 'changesync/stakeholders/UPDATE_SEARCH_QUERY';
const SEARCH_STAKEHOLDERS = 'changesync/stakeholders/SEARCH_STAKEHOLDERS';

// Initial State

const initialState = {
  /**
   * @property {object} stakeholderIds
   * Arrays of stakeholder IDs indexed by project ID
   * @example
   * {
   *   [projectId]: [stakeholderId, stakeholderId, ...]
   * }
   */
  stakeholderIds: {},
  stakeholderDraftId: null,
  query: '',
  pending: false,
  success: false,
  error: null,
};

// Reducer

export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_STAKEHOLDERS_REQUEST:
      return {
        ...state,
        pending: true,
        success: false,
        error: null,
      };
    case RETRIEVE_STAKEHOLDERS_SUCCESS:
      return {
        ...state,
        stakeholderIds: {
          ...state.stakeholderIds,
          [action.meta.projectId]: action.payload.result,
        },
        pending: false,
        success: true,
      };

    case CREATE_UPDATE_STAKEHOLDER_DRAFT_SUCCESS:
      return {
        ...state,
        stakeholderDraftId: action.payload.result,
      };

    case FINISH_UPDATE_STAKEHOLDER_DRAFT_SUCCESS:
      return {
        ...state,
        stakeholderDraftId: null,
      };

    case DELETE_STAKEHOLDER_SUCCESS:
      return {
        ...state,
        stakeholderDraftId: null,
      };

    case RESET_DRAFT_ID:
      return {
        ...state,
        stakeholderDraftId: null,
      };

    case SEARCH_STAKEHOLDERS_REQUEST:
      return {
        ...state,
        query: action.meta.query || '',
        pending: true,
        success: false,
        error: null,
      };

    case SEARCH_STAKEHOLDERS_SUCCESS:
      return {
        ...state,
        stakeholderIds: {
          ...state.stakeholderIds,
          [action.meta.projectId]: action.payload.result,
        },
        pending: false,
        success: true,
      };

    case UPDATE_SEARCH_QUERY:
      return {
        ...state,
        query: action.payload,
      };

    default:
      return state;
  }
};

// Action Creators

export const createStakeholderDraft = (
  projectId,
  stakeholderId,
  stakeholder,
  editing
) => {
  const url = editing
    ? `stakeholders/${stakeholderId}`
    : `projects/${projectId}/stakeholders`;
  const method = editing ? HTTP_METHODS.PUT : HTTP_METHODS.POST;
  return {
    [CALL_API]: {
      types: [
        CREATE_UPDATE_STAKEHOLDER_DRAFT_REQUEST,
        CREATE_UPDATE_STAKEHOLDER_DRAFT_SUCCESS,
        CREATE_UPDATE_STAKEHOLDER_DRAFT_FAILURE,
      ],
      endpoint: url,
      method,
      schema: stakeholderDraftSchema,
      body: { stakeholder },
      disabledDecamelizeKeys: true, // we need to prevent any key conversion and send them "as is"
      disabledCamelizeKeys: true,
      query: {
        delete_draft: !!editing,
      },
    },
  };
};

export const updateStakeholderDraft = (id, stakeholder, isCsv) => {
  return {
    [CALL_API]: {
      types: [
        CREATE_UPDATE_STAKEHOLDER_DRAFT_REQUEST,
        CREATE_UPDATE_STAKEHOLDER_DRAFT_SUCCESS,
        CREATE_UPDATE_STAKEHOLDER_DRAFT_FAILURE,
      ],
      endpoint: `stakeholders/${id}`,
      method: HTTP_METHODS.PUT,
      schema: stakeholderDraftSchema,
      body: isCsv ? stakeholder : { stakeholder },
      disabledDecamelizeKeys: true,
      disabledCamelizeKeys: true,
    },
  };
};

export const finishUpdateStakeholderDraft = (
  id,
  stakeholder,
  isCsv,
  deleteDraft
) => {
  return {
    [CALL_API]: {
      types: [
        FINISH_UPDATE_STAKEHOLDER_DRAFT_REQUEST,
        FINISH_UPDATE_STAKEHOLDER_DRAFT_SUCCESS,
        FINISH_UPDATE_STAKEHOLDER_DRAFT_FAILURE,
      ],
      endpoint: `stakeholders/${id}`,
      method: HTTP_METHODS.PUT,
      schema: stakeholderDraftSchema,
      body: isCsv ? stakeholder : { stakeholder },
      disabledDecamelizeKeys: true,
      disabledCamelizeKeys: true,
      query: {
        finish: true,
        delete_draft: !!deleteDraft,
      },
    },
  };
};

export const resetDraftId = () => ({
  type: RESET_DRAFT_ID,
});

export const updateStakeholderMapPosition = (id, stakeholder) => ({
  [CALL_API]: {
    types: [
      UPDATE_STAKEHOLDER_POSITION_REQUEST,
      UPDATE_STAKEHOLDER_POSITION_SUCCESS,
      UPDATE_STAKEHOLDER_POSITION_FAILURE,
    ],
    endpoint: `stakeholders/${id}/update_position`,
    method: HTTP_METHODS.PUT,
    schema: stakeholderSchema,
    body: { stakeholder },
    disabledDecamelizeKeys: true,
    disabledCamelizeKeys: true,
  },
});

export const retrieveStakeholders = (projectId) => (dispatch) => {
  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_STAKEHOLDERS_REQUEST,
        RETRIEVE_STAKEHOLDERS_SUCCESS,
        RETRIEVE_STAKEHOLDERS_FAILURE,
      ],
      endpoint: `projects/${projectId}/stakeholders`,
      method: HTTP_METHODS.GET,
      schema: [stakeholderSchema],
      disabledCamelizeKeys: true, // we need to prevent any key conversion and send them "as is"
    },
    meta: {
      projectId,
    },
  });
};

export const searchStakeholders = (projectId, query) => (dispatch) => {
  const searchParam = query ? `?search=&search=${query}` : '';

  return dispatch({
    [CALL_API]: {
      types: [
        SEARCH_STAKEHOLDERS_REQUEST,
        SEARCH_STAKEHOLDERS_SUCCESS,
        SEARCH_STAKEHOLDERS_FAILURE,
      ],
      endpoint: `projects/${projectId}/stakeholders${searchParam}`,
      method: HTTP_METHODS.GET,
      schema: [stakeholderSchema],
      disabledCamelizeKeys: true, // we need to prevent any key conversion and send them "as is"
    },
    meta: {
      projectId,
      query,
      debounce: {
        time: 400,
        key: SEARCH_STAKEHOLDERS,
      },
    },
  });
};

export const updateSearchQuery = (query) => ({
  type: UPDATE_SEARCH_QUERY,
  payload: query,
});

export const deleteStakeholder = (id) => ({
  [CALL_API]: {
    types: [
      DELETE_STAKEHOLDER_REQUEST,
      DELETE_STAKEHOLDER_SUCCESS,
      DELETE_STAKEHOLDER_FAILURE,
    ],
    endpoint: `stakeholders/${id}`,
    method: HTTP_METHODS.DELETE,
  },
});

export const downloadPdfReport = (projectId, query) => (dispatch) => {
  const url = `projects/${projectId}/stakeholders/pdf`;

  const { status, ...params } = query;

  return dispatch({
    [CALL_API]: {
      types: [
        DOWNLOAD_REPORT_REQUEST,
        DOWNLOAD_REPORT_SUCCESS,
        DOWNLOAD_REPORT_FAILURE,
      ],
      endpoint: url,
      method: HTTP_METHODS.GET,
      query: {
        ...params,
      },
    },
  });
};

// Selectors

const selectStakeholderId = (_, props) => props.stakeholderId;
const selectStakeholderIds = (state, props) =>
  state.stakeholders.stakeholderIds[props.projectId];
export const selectSearchRetrieveStakeholdersState = (state) =>
  state.stakeholders;

export const selectStakeholder = createSelector(
  selectStakeholderId,
  selectEntities,
  (stakeholderId, entities) =>
    denormalize(stakeholderId, stakeholderSchema, entities)
);

export const selectStakeholders = createSelector(
  selectStakeholderIds,
  selectEntities,
  (stakeholderIds, entities) =>
    denormalize(stakeholderIds, [stakeholderSchema], entities)
);

export const selectStakeholderDraftId = (state) =>
  state.stakeholders.stakeholderDraftId;
export const selectStakeholderDraft = (id) => (state) =>
  denormalize(id, stakeholderDraftSchema, state.entities);
